import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '../..';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { PartnerThirdPartyBillingAccountSet } from '@shipium-corp/shipium-ts-types';

export interface ThirdPartyBillingAccountSetsModel {
  thirdPartyBillingAccountSetsMap: Record<string, Required<PartnerThirdPartyBillingAccountSet>>;
  setThirdPartyBillingAccountSets: Action<
    ThirdPartyBillingAccountSetsModel,
    Required<PartnerThirdPartyBillingAccountSet>[]
  >;
  setThirdPartyBillingAccountSet: Action<
    ThirdPartyBillingAccountSetsModel,
    Required<PartnerThirdPartyBillingAccountSet>
  >;
  fetchThirdPartyBillingAccountSetsLoading: boolean;
  setFetchThirdPartyBillingAccountSetsLoading: Action<ThirdPartyBillingAccountSetsModel, boolean>;
  fetchThirdPartyBillingAccountSets: Thunk<
    ThirdPartyBillingAccountSetsModel,
    void,
    Injections,
    StoreModel,
    Promise<void>
  >;
  fetchThirdPartyBillingAccountSetsFailure: Action<ThirdPartyBillingAccountSetsModel, ApplicationErrorOptions>;
}

export const thirdPartyBillingAccountSetsModel: ThirdPartyBillingAccountSetsModel = {
  thirdPartyBillingAccountSetsMap: {},
  setThirdPartyBillingAccountSets: action((state, thirdPartyBillingAccountSets) => {
    const thirdPartyBillingAccountSetsMap = thirdPartyBillingAccountSets.reduce<
      Record<string, Required<PartnerThirdPartyBillingAccountSet>>
    >((acc, curr) => {
      acc[curr.thirdPartyBillingAccountSetId] = curr;
      return acc;
    }, {});
    state.thirdPartyBillingAccountSetsMap = thirdPartyBillingAccountSetsMap;
  }),
  setThirdPartyBillingAccountSet: action((state, thirdPartyBillingAccountSet) => {
    state.thirdPartyBillingAccountSetsMap[thirdPartyBillingAccountSet.thirdPartyBillingAccountSetId] =
      thirdPartyBillingAccountSet;
  }),
  fetchThirdPartyBillingAccountSetsLoading: false,
  setFetchThirdPartyBillingAccountSetsLoading: action((state, isLoading) => {
    state.fetchThirdPartyBillingAccountSetsLoading = isLoading;
  }),
  fetchThirdPartyBillingAccountSets: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchThirdPartyBillingAccountSetsLoading(true);
      const { fulfillmentContextService } = injections;
      const response = await fulfillmentContextService.fetchThirdPartyBillingAccountSets();
      actions.setThirdPartyBillingAccountSets(response);
      actions.setFetchThirdPartyBillingAccountSetsLoading(false);
      return response;
    } catch (error) {
      actions.fetchThirdPartyBillingAccountSetsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchThirdPartyBillingAccountSetsLoading(false);
      return error;
    }
  }),
  fetchThirdPartyBillingAccountSetsFailure: action((state, error) => {
    console.error(error);
  }),
};

export default thirdPartyBillingAccountSetsModel;
